import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../../types/shared/UUID';
import { useListAdminStandardDocumentsQuery } from '../../../../../../../API/order/documents/DocumentApi';
import { useCreateSigningAppointmentDocumentMutation } from '../../../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { SelectOption } from '../../../../../../../tailwindComponents/selectTW/interfaces';
import { ICreateSigningAppointmentDocumentRequest } from '../../../../../../../types/signingAppointment/ISigningAppointmentDocumentResponse';
import { WideModalTW } from '../../../../../../../tailwindComponents/modals/SimpleModalTW';
import { SimpleGrid } from '../../../../../../../tailwindComponents/grid/SimpleGrid';
import { Form, Formik } from 'formik';
import { SelectFieldTW } from '../../../../../../../tailwindComponents/selectTW/SelectFieldTW';
import { ButtonTW } from '../../../../../../../tailwindComponents/buttonTW/ButtonTW';
import * as yup from 'yup';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { sortByCreatedAtDescending } from '../../../../../fees/SortByCreatedAt';
import { useSelectAppointmentFromPathParam } from '../../../../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import { ISigningAppointmentResponse } from '../../../../../../../types/signingAppointment/ISigningAppointmentResponse';
import { IStandardDocumentVersionData } from '../../../../../../../types/order/documents/shared/interfaces';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../../../../API/documentCategories/DocumentCategoriesApi';

const isNotPreviouslyAssociated = (
  { standardDocumentId }: IStandardDocumentVersionData,
  signingAppointment?: ISigningAppointmentResponse
): boolean => {
  const documents = signingAppointment?.documents ?? [];
  return documents.every(({ standardDocument }) => standardDocument.id !== standardDocumentId);
};

export const CreateSigningAppointmentDocumentModal: React.FC<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ open, setOpen }) => {
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId: UUID }>();
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();

  const { data: orderDocuments = [] } = useListAdminStandardDocumentsQuery({ orderId });
  const [createSigningAppointmentDocument] = useCreateSigningAppointmentDocumentMutation();
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();

  const options: SelectOption[] = useMemo(() => {
    return orderDocuments
      .filter(({ category }) => documentCategoriesByName[category]?.isRepeatable)
      .filter((standardDocumentVersionData) =>
        isNotPreviouslyAssociated(standardDocumentVersionData, appointmentFromPath)
      )
      .toSorted(sortByCreatedAtDescending)
      .map(({ fileName, id }) => ({ label: fileName, value: id }));
  }, [orderDocuments, documentCategoriesByName, appointmentFromPath]);

  const initialValues: ICreateSigningAppointmentDocumentRequest = {
    orderId,
    signingAppointmentId: appointmentId,
    standardDocumentId: '',
  };

  const onCreateSigningAppointmentDocument = async (values: ICreateSigningAppointmentDocumentRequest) => {
    const res = await createSigningAppointmentDocument(values);
    checkForMutationSuccess(res, 'document association');
    setOpen(false);
  };

  return (
    <WideModalTW open={open} setOpen={setOpen}>
      <SimpleGrid className={'w-full'}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => onCreateSigningAppointmentDocument(values)}
        >
          {({ isValid, isSubmitting }) => (
            <Form>
              <SimpleGrid>
                <SelectFieldTW name={'standardDocumentId'} options={options} labelText={'Order Documents'} />
                <div className={'flex flex-row-reverse gap-4'}>
                  <ButtonTW type={'submit'} disabled={!isValid || isSubmitting}>
                    Associate with this Signing Appointment
                  </ButtonTW>
                  <ButtonTW type={'button'} secondary onClick={() => setOpen(false)}>
                    Cancel
                  </ButtonTW>
                </div>
              </SimpleGrid>
            </Form>
          )}
        </Formik>
      </SimpleGrid>
    </WideModalTW>
  );
};

const validationSchema = yup.object().shape({
  standardDocumentId: yup.string().uuid().required('Must choose a document!'),
  signingAppointmentId: yup.string().uuid().required(),
});
